
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useFlightUpdates } from '@/hooks/useFlightUpdates';
import { RefreshCw, Clock, AlertTriangle, Calendar, Info } from 'lucide-react';
import { formatTime } from '@/lib/formatters';

export function FlightUpdatesLog() {
  const { updates, fetchUpdates } = useFlightUpdates();

  const getUpdateTypeIcon = (type: string) => {
    switch (type) {
      case 'status':
        return <AlertTriangle className="h-4 w-4" />;
      case 'schedule':
        return <Calendar className="h-4 w-4" />;
      case 'details':
        return <Info className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getUpdateTypeBadge = (type: string) => {
    const variants = {
      status: 'destructive' as const,
      schedule: 'secondary' as const,
      details: 'outline' as const,
      general: 'default' as const,
    };
    
    return (
      <Badge variant={variants[type as keyof typeof variants] || variants.general}>
        {getUpdateTypeIcon(type)}
        <span className="ml-1 capitalize">{type}</span>
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Real-time Flight Updates
            </CardTitle>
            <CardDescription>
              Monitor all flight changes and updates across organizations
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchUpdates}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {updates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No flight updates logged yet
            </div>
          ) : (
            updates.map((update) => (
              <div 
                key={update.id} 
                className="border rounded-lg p-4 space-y-3 bg-card hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        Flight {(update as any).flights?.flight_number}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        by {(update as any).flights?.organization?.name}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {update.change_summary}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getUpdateTypeBadge(update.update_type)}
                    <span className="text-xs text-muted-foreground">
                      {formatTime(update.created_at)}
                    </span>
                  </div>
                </div>
                
                {/* Show changes details */}
                <div className="grid gap-2 md:grid-cols-2 text-xs">
                  {Object.keys(update.previous_values || {}).length > 0 && (
                    <div className="bg-red-50 dark:bg-red-950/20 p-2 rounded border">
                      <strong className="text-red-700 dark:text-red-400">Previous:</strong>
                      <div className="mt-1 space-y-1">
                        {Object.entries(update.previous_values || {}).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium">{key}:</span> {String(value)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {Object.keys(update.new_values || {}).length > 0 && (
                    <div className="bg-green-50 dark:bg-green-950/20 p-2 rounded border">
                      <strong className="text-green-700 dark:text-green-400">New:</strong>
                      <div className="mt-1 space-y-1">
                        {Object.entries(update.new_values || {}).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium">{key}:</span> {String(value)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
